import type { GeofenceProps, GeoPayload } from '../lib/types'

/** `source_folder` is on every live geofence feature (confirmed against production
 *  2026-10-12 -- SPEC.md "Verified APIs") but is not declared on `GeofenceProps` in
 *  lib/types.ts. Carried here as an intersection rather than edited into the shared type,
 *  which is outside this change's declared blast radius. */
type LiveGeofenceProps = GeofenceProps & { source_folder?: string | null }

const EMPTY_ROUTES: GeoPayload['routes'] = { type: 'FeatureCollection', features: [] }
const EMPTY_GEOFENCES: GeoPayload['geofences'] = { type: 'FeatureCollection', features: [] }

/**
 * Wonder Cement's own slice of the global `/api/geo` payload every fleet shares.
 *
 * `GET /api/geo` has no fleet filter server-side -- its `routes`/`geofences`
 * FeatureCollections mix Ultratech's `dhar-dhule-*` geometry with Wonder Cement's
 * `wondercement-*` geometry in the same arrays. `useFleet`'s `routes`/`geofences` store
 * fields carry that same unfiltered payload. This is the one place that split happens,
 * shared by `WonderCementShell` (its own GEOFENCES tab) and `WonderCementMap` (the route
 * line + fence layers), so there is exactly one definition of "Wonder Cement's own" rather
 * than two that could drift apart.
 *
 * A feature of unknown provenance (no `route_id`, no `source_folder`) is dropped, not
 * admitted -- AC-2's "Ultratech's 25 fences must never render on Wonder Cement's map" is
 * the stricter requirement, and unknown provenance isn't Wonder Cement's.
 */
export function wondercementGeo(
  routes: GeoPayload['routes'] | null,
  geofences: GeoPayload['geofences'] | null,
): GeoPayload {
  const routeFeatures = (routes?.features ?? []).filter(
    (f) => f.properties.route_id?.startsWith('wondercement-') ?? false,
  )
  const geofenceFeatures = (geofences?.features ?? []).filter((f) => {
    const folder = (f.properties as LiveGeofenceProps).source_folder
    return !!folder && folder.startsWith('Wonder Cement')
  })

  return {
    routes: routeFeatures.length ? { type: 'FeatureCollection', features: routeFeatures } : EMPTY_ROUTES,
    geofences: geofenceFeatures.length
      ? { type: 'FeatureCollection', features: geofenceFeatures } as GeoPayload['geofences']
      : EMPTY_GEOFENCES,
    counts: { routes: routeFeatures.length, geofences: geofenceFeatures.length },
    // Nothing in the route/fence layer path (console/src/map/layers/reference.ts) reads
    // `tiles` -- it exists purely to satisfy GeoPayload's shape. The field's real readers
    // (state/useFleetData.ts's "basemap not installed" toast, state/store.ts's
    // `hasTiles`) are driven off the GLOBAL /api/geo response, not this filtered slice, so
    // this is always false rather than guessed from the Ultratech-corridor-scoped payload.
    tiles: false,
  }
}
